// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductBadges from "@guestsupply/App/Components/Common/ProductBadges";
import AutoDeliveryLink from "@guestsupply/App/Components/Product/AutoDeliveryLink";
import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@guestsupply/App/Components/Product/ProductGridContainer";
import xUseWidgetBadges from "@guestsupply/App/Hooks/xUseWidgetBadges";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { mergeWithCss, parseBoolean } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    product: ProductModel;
    widgetId: string;
    showImage?: boolean;
    showTitle?: boolean;
    showPartNumbers?: boolean;
    showPrice?: boolean;
    showAvailability?: boolean;
    showAddToCart?: boolean;
    showAddToList?: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductMiniCardStyles;
    // xng-change: add google event
    handleClickedLink?: (title: string, url: string) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productSettings: getSettingsCollection(state).productSettings,
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    isAuthenticated: xIsAuthenticated(state),
    cannotProp: getSession(state).properties?.cannotCreateRecurringOrders,
    isPunchout: getSettingsCollection(state).customSettings.isPunchout,
});

const mapDispatchToProps = {
    updateProductInfo,
};

export interface ProductMiniCardStyles {
    productGridContainerStyles?: ProductGridContainerStyle;
    imageAndDescriptionSection?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceStyles?: ProductPriceStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    productActions?: ProductActionsStyle;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    autoDeliveryWrapper?: InjectableCss;
    availabilityyWrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
    productAvailabilityStyle?: ProductAvailabilityStyles;
}

// Xng-Changes: please check ProductMiniCardExtensions.ts for styles
export const productMiniCardStyles: ProductMiniCardStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ProductMiniCard = ({
    product,
    productInfo,
    widgetId,
    showImage,
    showTitle,
    showPartNumbers,
    showPrice,
    showAvailability,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    extendedStyles,
    productSettings,
    updateProductInfo,
    cannotProp,
    isAuthenticated,
    isPunchout,
    // xng-change: add google event
    handleClickedLink = () => {},
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productMiniCardStyles, extendedStyles));
    if (!productInfo || !product) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
        },
        onQtyOrderedChanged: qtyOrdered => {
            updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
        },
    };

    const cannotAutoDeliver = () => {
        return cannotProp && parseBoolean(cannotProp) && isPunchout;
    };

    const { ootbBadgesAndTags, customBadgesAndTags } = xUseWidgetBadges({
        productContext,
        showTextBadges,
        showImageBadges,
    });

    const subscriptionFlag = productContext.product.properties?.subscription_Flag?.toLocaleLowerCase();

    const enableAutoDelivery = isAuthenticated && subscriptionFlag === "true" && !cannotAutoDeliver();

    return (
        <ProductContext.Provider value={productContext}>
            <ProductGridContainer extendedStyles={styles.productGridContainerStyles}>
                <ProductGridContainer.Info>
                    <StyledWrapper {...styles.imageAndDescriptionSection}>
                        {showImage && (
                            <ProductGridContainer.Image>
                                <ProductImage
                                    product={productContext}
                                    showSmallImage={true}
                                    extendedStyles={styles.productImage}
                                    {...{ showPreviouslyPurchasedTag: true, isGridView: false }}
                                    // xng-change: add google event
                                    onClick={() =>
                                        handleClickedLink(
                                            productContext.product.productTitle,
                                            productContext.productInfo.productDetailPath,
                                        )
                                    }
                                />
                            </ProductGridContainer.Image>
                        )}
                        <StyledWrapper className="product-description">
                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescription}
                                    // xng-change: add google event
                                    onClick={() =>
                                        handleClickedLink(
                                            productContext.product.productTitle,
                                            productContext.productInfo.productDetailPath,
                                        )
                                    }
                                />
                            )}
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    showCustomerName={false}
                                    showManufacturerItem={false}
                                    showLabel
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                            <ProductBadges
                                ootbBadgesAndTags={ootbBadgesAndTags}
                                customBadgesAndTags={customBadgesAndTags}
                            />
                            {showPrice && productSettings.canSeePrices && (
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showSavings={true}
                                    showPack={false}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            )}
                            <StyledWrapper>
                                <StyledWrapper {...styles.availabilityyWrapper}>
                                    {showAvailability && (
                                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyle} />
                                    )}
                                </StyledWrapper>
                                {isAuthenticated && (
                                    <StyledWrapper {...styles.autoDeliveryWrapper}>
                                        {enableAutoDelivery && <AutoDeliveryLink />}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </ProductGridContainer.Info>
                <ProductGridContainer.AddToCartActions>
                    {showAddToCart && <ProductActions extendedStyles={styles.productActions} showDisabled />}
                </ProductGridContainer.AddToCartActions>
                <ProductGridContainer.AddToList>
                    {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListLinkStyles} />}
                </ProductGridContainer.AddToList>
            </ProductGridContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductMiniCard);
